import { FC, ReactNode, useState } from 'react';
import { Box, ButtonBase, Collapse, Stack, Typography } from '@mui/material';

import { ChevronIcon } from '../../icons';

interface ITicketDetailSectionProps {
	icon: ReactNode;
	title: string;
	/**
	 * Subtitle stacked directly under the title, beside the icon (8px gap),
	 * instead of as a full-width row below the header. Used by the operator
	 * header where the logo sits left of a title/description stack. Hidden while
	 * the section is open. Mutually exclusive with `collapsedSubtitle`.
	 */
	headerSubtitle?: ReactNode;
	collapsedSubtitle?: ReactNode;
	children?: ReactNode;
	defaultOpen?: boolean;
	disableToggle?: boolean;
	withTopDivider?: boolean;
	dataTestId?: string;
	/** Controlled open state. When provided the section is controlled and `onToggle` is called on header tap. */
	open?: boolean;
	/** Called on header tap when the section is controlled (i.e. `open` is provided). */
	onToggle?: () => void;
}

export const TicketDetailSection: FC<ITicketDetailSectionProps> = ({
	icon,
	title,
	headerSubtitle,
	collapsedSubtitle,
	children,
	defaultOpen = false,
	disableToggle = false,
	withTopDivider = false,
	dataTestId,
	open,
	onToggle,
}) => {
	const [internalOpen, setInternalOpen] = useState(defaultOpen);
	const isControlled = open !== undefined;
	const isOpen = isControlled ? open : internalOpen;
	const canToggle = !disableToggle && !!children;

	const handleToggle = () => {
		if (!canToggle) return;
		if (isControlled) onToggle?.();
		else setInternalOpen((prev) => !prev);
	};

	const containerSx = {
		width: 'auto',
		mx: -2,
		pt: 2,
		pb: 1,
		px: 2,
		...(withTopDivider && {
			borderTop: (theme: { palette: { neutral?: { 200?: string } } }) =>
				`1px solid ${theme.palette.neutral?.[200] ?? '#b2b2b2'}`,
		}),
	};

	const headerRow = (
		<Stack
			direction="row"
			alignItems={headerSubtitle ? 'flex-start' : 'center'}
			gap={headerSubtitle ? 1 : 0.5}
			sx={{ minHeight: 24, width: '100%' }}
		>
			<Box sx={{ display: 'flex', flexShrink: 0 }}>{icon}</Box>
			{headerSubtitle ? (
				<Stack gap={1} sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						variant="h5-sb"
						color="primary.main"
						sx={{ textAlign: 'left' }}
					>
						{title}
					</Typography>
					<Collapse in={!isOpen} timeout={200}>
						{headerSubtitle}
					</Collapse>
				</Stack>
			) : (
				<Typography
					variant="h5-sb"
					color="primary.main"
					sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}
				>
					{title}
				</Typography>
			)}
			{canToggle && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexShrink: 0,
						width: 24,
						height: 24,
					}}
				>
					<ChevronIcon
						direction={isOpen ? 'up' : 'down'}
						sx={{ fontSize: 16, color: 'solutions.600' }}
					/>
				</Box>
			)}
		</Stack>
	);

	const collapsedSummary = collapsedSubtitle && (
		<Collapse in={!isOpen} timeout={200} sx={{ width: '100%' }}>
			<Box sx={{ width: '100%', pt: 1 }}>{collapsedSubtitle}</Box>
		</Collapse>
	);

	const header = canToggle ? (
		<ButtonBase
			onClick={handleToggle}
			disableRipple
			sx={{ width: '100%', display: 'block', textAlign: 'left' }}
		>
			{headerRow}
			{collapsedSummary}
		</ButtonBase>
	) : (
		<Box sx={{ width: '100%' }}>
			{headerRow}
			{collapsedSummary}
		</Box>
	);

	return (
		<Box sx={containerSx} data-testid={dataTestId}>
			<Stack sx={{ alignItems: 'flex-start', width: '100%' }}>
				{header}
				<Collapse in={isOpen} timeout={200} sx={{ width: '100%' }}>
					<Stack
						gap={1}
						sx={{ width: '100%', alignItems: 'flex-start', pt: 1 }}
					>
						{children}
					</Stack>
				</Collapse>
			</Stack>
		</Box>
	);
};
